import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { Button } from 'primeng/button';

export type BdProgressState = 'idle' | 'running' | 'done';

/**
 * What a batch reports, whoever runs it: `completed` counts attempts — failed
 * ones included — so it reaches `total` even when everything failed.
 */
export interface BdProgressCounts {
  state: BdProgressState;
  completed: number;
  total: number;
  failed: number;
}

/**
 * Which shape the indicator takes. All three are the same data and the same
 * colour logic — only the presentation differs, so a caller can move between
 * them without changing anything else.
 *
 * - `card` — concept 1a: headline percentage, split bar, status block. The
 *   default; use it when the progress view *is* the content of the surface.
 * - `ring` — concept 1b: a donut with the percentage in its centre and a
 *   success/failure legend. Same footprint in both directions, so it suits a
 *   square slot or a summary panel.
 * - `compact` — the concept's "Kompaktvariante — Tabellen- und Kopfzeilen": a
 *   single 6 px row. For dense contexts (a table caption, a section header)
 *   where the progress annotates something else rather than being the subject.
 */
export type BdProgressMode = 'card' | 'ring' | 'compact';

/** Which outcome the indicator is showing — decides the accent and the icon. */
type BdProgressOutcome = 'running' | 'success' | 'error';

/** Radius of the 1b donut, in the SVG's own 180×180 user space. */
const RING_RADIUS = 78;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/**
 * Progress view for a batch of attempts — moving or sharing shareables, the
 * members of a job chain — built to the "Bearbeitungsstand" design concept.
 * `mode` picks between the concept's three presentations (see
 * {@link BdProgressMode}); everything below is shared by all of them.
 *
 * The idea the concept turns on, and this implementation keeps: the failed
 * share stays **inside the same track** as the successful one, so a run that
 * finished with errors cannot read as a clean success. `completedCount` counts
 * *attempts*, so it reaches the total even when everything failed — the track
 * is therefore split by outcome rather than driven by that one number.
 *
 * Two deliberate departures from the concept file:
 *
 * 1. **Colours come from theme tokens, not the concept's hex values.** The
 *    concept is drawn in indigo/green/red; this app's accent is the brand
 *    primary, and the styleguide forbids raw hex. The *logic* is preserved —
 *    one accent that follows the state, with the failed share always red.
 * 2. **The bar is not `p-progressbar`.** That component renders a single value
 *    and so cannot show two shares in one track, which is the whole point of
 *    the concept. The track is hand-built with the same ARIA role a progress
 *    bar has.
 */
@Component({
  selector: 'bd-ui-progress-indicator',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @switch (state()) {
      @case ('idle') {
        <ng-content />
      }
      @default {
        @switch (mode()) {
          @case ('compact') {
            <!--
              One row: track, share, count, and the failures — nothing else.
              No status text and no close button; in the places this mode is
              made for, the surrounding view already says what is going on and
              owns its own footer.
            -->
            <div
              class="bd-progress__compact"
              role="progressbar"
              [attr.aria-valuenow]="percentage()"
              aria-valuemin="0"
              aria-valuemax="100"
              [attr.aria-valuetext]="ariaValueText()"
              [attr.aria-label]="title()"
            >
              <div class="bd-progress__track bd-progress__track--compact">
                <div
                  class="bd-progress__fill"
                  [style.width.%]="successPercent()"
                  [style.background]="successColor()"
                ></div>
                <div
                  class="bd-progress__fill bd-progress__fill--error"
                  [style.width.%]="errorPercent()"
                ></div>
              </div>
              <span class="bd-progress__compact-percent">{{
                percentLabel()
              }}</span>
              <span class="bd-progress__compact-count">{{ countShort() }}</span>
              @if (errorCount() > 0) {
                <span class="bd-progress__compact-errors">{{
                  errorInline()
                }}</span>
              }
            </div>
          }

          @case ('ring') {
            <div class="flex flex-col gap-5 py-4">
              <span class="bd-progress__eyebrow">{{ title() }}</span>

              <!--
                The ring and its legend share one narrow, centred column so the
                legend cannot stretch away from the 180 px donut on a wide
                surface. The eyebrow and the footer stay full width, as in the
                other modes.
              -->
              <div class="bd-progress__ring-column">
                <div
                  class="bd-progress__ring"
                  role="progressbar"
                  [attr.aria-valuenow]="percentage()"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  [attr.aria-valuetext]="ariaValueText()"
                  [attr.aria-label]="title()"
                >
                  <svg viewBox="0 0 180 180" aria-hidden="true">
                    <circle
                      class="bd-progress__ring-track"
                      cx="90"
                      cy="90"
                      [attr.r]="ringRadius"
                      fill="none"
                      stroke-width="12"
                    ></circle>
                    <circle
                      class="bd-progress__ring-arc"
                      cx="90"
                      cy="90"
                      [attr.r]="ringRadius"
                      fill="none"
                      stroke-width="12"
                      [attr.stroke]="successColor()"
                      [attr.stroke-dasharray]="ringSuccessDash()"
                    ></circle>
                    <!--
                    The failed arc is offset by the successful one so the two
                    sit end to end on the same circle, mirroring how they share
                    the bar in the other modes.
                  -->
                    <circle
                      class="bd-progress__ring-arc bd-progress__ring-arc--error"
                      cx="90"
                      cy="90"
                      [attr.r]="ringRadius"
                      fill="none"
                      stroke-width="12"
                      [attr.stroke-dasharray]="ringErrorDash()"
                      [attr.stroke-dashoffset]="ringErrorOffset()"
                    ></circle>
                  </svg>
                  <div class="bd-progress__ring-center">
                    <span class="bd-progress__ring-percent">{{
                      percentLabel()
                    }}</span>
                    <span class="bd-progress__ring-count">{{
                      countShort()
                    }}</span>
                  </div>
                </div>

                <div class="flex flex-col gap-2 w-full">
                  <div class="bd-progress__legend-row">
                    <span class="bd-progress__legend-label">
                      <span
                        class="bd-progress__legend-dot"
                        [style.background]="successColor()"
                      ></span>
                      Erfolgreich
                    </span>
                    <span class="bd-progress__legend-value">{{
                      successLabel()
                    }}</span>
                  </div>
                  <div class="bd-progress__legend-row">
                    <span class="bd-progress__legend-label">
                      <span
                        class="bd-progress__legend-dot bd-progress__legend-dot--error"
                      ></span>
                      Fehlerhaft
                    </span>
                    <span
                      class="bd-progress__legend-value"
                      [style.color]="errorTextColor()"
                      >{{ errorLabel() }}</span
                    >
                  </div>
                </div>
              </div>

              @if (state() === 'done' && showCloseButton()) {
                <div class="flex flex-row justify-end w-full">
                  <p-button
                    label="Schließen"
                    severity="primary"
                    (onClick)="closed.emit()"
                  />
                </div>
              }
            </div>
          }

          @default {
            <div class="flex flex-col gap-5 py-4">
              <!-- Header: label and counts left, the share as one large number right. -->
              <div class="flex flex-row items-baseline justify-between gap-4">
                <div class="flex flex-col gap-1">
                  <span class="bd-progress__eyebrow">{{ title() }}</span>
                  <span class="bd-progress__count">{{ countLine() }}</span>
                </div>
                <span
                  class="bd-progress__percent"
                  [style.color]="accentColor()"
                  >{{ percentLabel() }}</span
                >
              </div>

              <div class="flex flex-col gap-2">
                <div
                  class="bd-progress__track"
                  role="progressbar"
                  [attr.aria-valuenow]="percentage()"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  [attr.aria-valuetext]="ariaValueText()"
                  [attr.aria-label]="title()"
                >
                  <div
                    class="bd-progress__fill"
                    [style.width.%]="successPercent()"
                    [style.background]="successColor()"
                  ></div>
                  <div
                    class="bd-progress__fill bd-progress__fill--error"
                    [style.width.%]="errorPercent()"
                  ></div>
                </div>
                <div class="flex flex-row justify-between bd-progress__scale">
                  <span>0</span>
                  <span>{{ totalLabel() }}</span>
                </div>
              </div>

              <!-- Status block: one line naming the outcome, one explaining it. -->
              <div class="flex flex-col gap-2 pt-4 bd-progress__divider">
                <div class="flex flex-row items-center gap-2">
                  <i [class]="statusIcon()" [style.color]="accentColor()"></i>
                  <span
                    class="bd-progress__status"
                    [style.color]="accentColor()"
                    >{{ statusLine() }}</span
                  >
                </div>
                <span class="bd-progress__detail">{{ statusDetail() }}</span>
              </div>

              @if (state() === 'done' && showCloseButton()) {
                <div class="flex flex-row justify-end w-full">
                  <p-button
                    label="Schließen"
                    severity="primary"
                    (onClick)="closed.emit()"
                  />
                </div>
              }
            </div>
          }
        }
      }
    }
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .bd-progress__eyebrow {
        font-size: 0.6875rem;
        line-height: 1rem;
        font-weight: 500;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--p-text-muted-color);
      }

      .bd-progress__count,
      .bd-progress__status,
      .bd-progress__detail {
        font-size: 0.8125rem;
        line-height: 1.25rem;
      }

      .bd-progress__count,
      .bd-progress__detail {
        color: var(--p-text-muted-color);
      }

      .bd-progress__status {
        font-weight: 500;
      }

      .bd-progress__percent {
        font-size: 3rem;
        line-height: 3rem;
        font-weight: 600;
        font-variant-numeric: tabular-nums;
      }

      .bd-progress__scale {
        font-size: 0.75rem;
        line-height: 1rem;
        color: var(--p-text-muted-color);
        font-variant-numeric: tabular-nums;
      }

      .bd-progress__divider {
        border-top: 1px solid var(--p-content-border-color);
      }

      .bd-progress__track {
        display: flex;
        height: 10px;
        border-radius: 9999px;
        background: var(--p-bd-surface-card);
        overflow: hidden;
      }

      .bd-progress__fill {
        flex: none;
        transition: width 240ms cubic-bezier(0.2, 0, 0, 1);
      }

      .bd-progress__fill--error {
        background: var(--p-red-500);
      }

      /* Compact mode — a single row for table captions and section headers. */
      .bd-progress__compact {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 1rem;
        font-variant-numeric: tabular-nums;
      }

      .bd-progress__track--compact {
        flex: 1;
        height: 6px;
      }

      .bd-progress__compact-percent,
      .bd-progress__compact-count,
      .bd-progress__compact-errors {
        font-size: 0.8125rem;
        line-height: 1.25rem;
      }

      .bd-progress__compact-percent {
        font-weight: 500;
        min-width: 52px;
        text-align: right;
      }

      .bd-progress__compact-count {
        color: var(--p-text-muted-color);
      }

      .bd-progress__compact-errors {
        font-weight: 500;
        color: var(--p-bd-status-error-color);
      }

      /* Ring mode — concept 1b. */
      .bd-progress__ring-column {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1.25rem;
        /* The concept draws 1b in a 300 px card; keep that proportion between
           the donut and its legend however wide the surface happens to be. */
        width: 100%;
        max-width: 300px;
        margin-inline: auto;
      }

      .bd-progress__ring {
        position: relative;
        width: 180px;
        height: 180px;
      }

      .bd-progress__ring svg {
        width: 180px;
        height: 180px;
        /* Start the arcs at twelve o'clock rather than three. */
        transform: rotate(-90deg);
      }

      .bd-progress__ring-track {
        stroke: var(--p-bd-surface-card);
      }

      .bd-progress__ring-arc {
        transition: stroke-dasharray 240ms cubic-bezier(0.2, 0, 0, 1);
      }

      .bd-progress__ring-arc--error {
        stroke: var(--p-red-500);
      }

      .bd-progress__ring-center {
        position: absolute;
        inset: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 2px;
      }

      .bd-progress__ring-percent {
        font-size: 2.5rem;
        line-height: 2.75rem;
        font-weight: 600;
        font-variant-numeric: tabular-nums;
      }

      .bd-progress__ring-count {
        font-size: 0.75rem;
        line-height: 1rem;
        color: var(--p-text-muted-color);
        font-variant-numeric: tabular-nums;
      }

      .bd-progress__legend-row {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
        font-size: 0.8125rem;
        line-height: 1.25rem;
      }

      .bd-progress__legend-label {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;
        color: var(--p-text-muted-color);
      }

      .bd-progress__legend-dot {
        flex: none;
        width: 8px;
        height: 8px;
        border-radius: 9999px;
      }

      .bd-progress__legend-dot--error {
        background: var(--p-red-500);
      }

      .bd-progress__legend-value {
        font-weight: 500;
        font-variant-numeric: tabular-nums;
      }

      /* The bar still lands on its value, it just does not travel there. */
      @media (prefers-reduced-motion: reduce) {
        .bd-progress__fill,
        .bd-progress__ring-arc {
          transition: none;
        }
      }
    `,
  ],
  imports: [Button],
})
export class BdUiProgressIndicatorComponent {
  public readonly state = input<BdProgressState>('idle');
  /** Presentation to render — see {@link BdProgressMode}. */
  public readonly mode = input<BdProgressMode>('card');
  public readonly actionLabel = input<string>('Vorgang läuft…');
  public readonly doneLabel = input<string>(
    'Vorgang erfolgreich abgeschlossen.',
  );
  /**
   * Small uppercase label above the counts. In `compact` mode there is no room
   * for it, so it becomes the bar's accessible name instead.
   */
  public readonly title = input<string>('Fortschritt');
  /** Attempts made — reaches `totalCount` even when every attempt failed. */
  public readonly completedCount = input<number>(0);
  public readonly totalCount = input<number>(0);
  public readonly errorCount = input<number>(0);
  /**
   * Whether the finished state offers its own "Schließen" button.
   *
   * Dialogs that end on this view need it. Callers that embed the indicator as
   * one section of a larger step — the move preview, for instance — already
   * have a footer of their own and pass `false`. Ignored in `compact` mode,
   * which is a bare row by definition.
   */
  public readonly showCloseButton = input(true);
  public readonly closed = output<void>();

  /** Radius of the 1b donut, exposed for the template's SVG bindings. */
  public readonly ringRadius = RING_RADIUS;

  public readonly percentage = computed(() => {
    const total = this.totalCount();

    return total === 0 ? 0 : Math.round((this.completedCount() / total) * 100);
  });

  /** Attempts that actually succeeded. */
  private readonly _successCount = computed(() =>
    Math.max(0, this.completedCount() - this.errorCount()),
  );

  private readonly _outcome = computed<BdProgressOutcome>(() => {
    if (this.state() !== 'done') {
      return 'running';
    }

    return this.errorCount() > 0 ? 'error' : 'success';
  });

  public readonly successPercent = computed(() =>
    this._share(this._successCount()),
  );
  public readonly errorPercent = computed(() => this._share(this.errorCount()));

  /**
   * The successful share turns green only once the run finished cleanly; while
   * it is still going the accent stays neutral-positive, as in the concept.
   */
  public readonly successColor = computed(() =>
    this._outcome() === 'success'
      ? 'var(--p-green-500)'
      : 'var(--p-primary-color)',
  );

  public readonly accentColor = computed(() => {
    switch (this._outcome()) {
      case 'error':
        return 'var(--p-bd-status-error-color)';
      case 'success':
        return 'var(--p-bd-status-success-color)';
      default:
        return this.percentage() > 0
          ? 'var(--p-primary-color)'
          : 'var(--p-text-muted-color)';
    }
  });

  /** Muted rather than red while there is nothing to warn about. */
  public readonly errorTextColor = computed(() =>
    this.errorCount() > 0 ? 'var(--p-bd-status-error-color)' : 'var(--p-text-muted-color)',
  );

  public readonly statusIcon = computed(() => {
    switch (this._outcome()) {
      case 'error':
        // Styleguide's fixed error glyph, in place of the concept's triangle.
        return 'pi pi-times-circle';
      case 'success':
        return 'pi pi-check-circle';
      default:
        // A spinner rather than the concept's clock: the operation is live.
        return 'pi pi-spin pi-spinner';
    }
  });

  public readonly statusLine = computed(() => {
    switch (this._outcome()) {
      case 'error':
        return 'Mit Fehlern';
      case 'success':
        return 'Abgeschlossen';
      default:
        return this.percentage() === 0 ? 'Nicht begonnen' : this.actionLabel();
    }
  });

  public readonly statusDetail = computed(() => {
    const failed = this.errorCount();

    if (this._outcome() === 'error') {
      return `${this._de(failed)} fehlgeschlagen · ${this._de(
        this._successCount(),
      )} erfolgreich`;
    }

    if (this._outcome() === 'success') {
      return this.doneLabel();
    }

    const remaining = Math.max(0, this.totalCount() - this.completedCount());

    return remaining === 0
      ? 'Keine offenen Elemente'
      : `${this._de(remaining)} verbleiben`;
  });

  public readonly percentLabel = computed(
    () => `${this._de(this.percentage())}\u00a0%`,
  );
  public readonly totalLabel = computed(() => this._de(this.totalCount()));
  public readonly countLine = computed(
    () =>
      `${this._de(this.completedCount())} von ${this._de(
        this.totalCount(),
      )} Elementen`,
  );

  /** "3 / 8" — the count line with no room for words. */
  public readonly countShort = computed(
    () => `${this._de(this.completedCount())} / ${this._de(this.totalCount())}`,
  );

  public readonly successLabel = computed(() => this._de(this._successCount()));
  public readonly errorLabel = computed(() => this._de(this.errorCount()));

  /** Failure count as it appears at the end of the compact row. */
  public readonly errorInline = computed(
    () => `${this._de(this.errorCount())} Fehler`,
  );

  /**
   * Spoken description of the bar. The bare percentage would hide the very
   * thing the split track exists to show, so the failures are named too.
   */
  public readonly ariaValueText = computed(() => {
    const base = `${this.countShort()} — ${this.statusLine()}`;

    return this.errorCount() > 0 ? `${base}, ${this.errorInline()}` : base;
  });

  public readonly ringSuccessDash = computed(() =>
    this._arc(this.successPercent()),
  );
  public readonly ringErrorDash = computed(() =>
    this._arc(this.errorPercent()),
  );

  /**
   * Negative offset pushes the failed arc past the successful one, so the two
   * meet instead of both starting at twelve o'clock.
   */
  public readonly ringErrorOffset = computed(
    () => -((RING_CIRCUMFERENCE * this.successPercent()) / 100),
  );

  /** `stroke-dasharray` drawing `percent` of the circle as one solid arc. */
  private _arc(percent: number): string {
    const length = (RING_CIRCUMFERENCE * percent) / 100;

    return `${length} ${RING_CIRCUMFERENCE}`;
  }

  private _share(count: number): number {
    const total = this.totalCount();

    return total === 0 ? 0 : (count / total) * 100;
  }

  private _de(value: number): string {
    return value.toLocaleString('de-DE');
  }
}
